import React, { ReactNode, createContext, useContext, useMemo, useReducer } from 'react';
import { useInterval } from '../utils/useInterval';

export type NotificationType = "none" | "success" | "error" | "warning" | "info" | undefined;
export interface Notification {
    text: string;
    page: string;
    type: NotificationType;
    time: number;
}

const NOTIFICATION_TIME = 7500;
type NotificationState = {
    popupNotifications: Notification[],
    allNotifications: Notification[],
};

type NotificationAPI = {
    clearNotifications: () => void;
    removeNotificationFromAll: (time: number) => void;
    removeNotificationFromPopup: (time: number) => void;
    addNotification: (text: string, page: string, type: NotificationType) => void;
    addNotifications: (notifications: Notification[]) => void;
};

const NotificationDataContext = createContext<NotificationState>({ popupNotifications: [], allNotifications: [] } as NotificationState);
const NotificationAPIContext = createContext<NotificationAPI>({} as NotificationAPI);

type NotificationActions =
    | { type: "clearNotifications"; }
    | { type: "removeNotificationFromAll"; time: number; }
    | { type: "removeNotificationFromPopup"; time: number; }
    | { type: "addNotification"; newNotification: Notification; }
    | { type: "addNotifications"; notifications: Notification[]; }

const removeItemByTime = (notifications: Notification[], time: number) => {
    let mutateArray = structuredClone(notifications);
    const index = mutateArray.findIndex(x => x.time === time);
    mutateArray.splice(index, 1);
    return (mutateArray);
}

const reducer = (state: NotificationState, action: NotificationActions): NotificationState => {
    switch (action.type) {
        case "clearNotifications":
            return { popupNotifications: [], allNotifications: [] };
        case "removeNotificationFromAll":
            return { ...state, allNotifications: removeItemByTime(state.allNotifications, action.time) };
        case "removeNotificationFromPopup":
            return { ...state, popupNotifications: removeItemByTime(state.popupNotifications, action.time) };
        case "addNotification":
            return { popupNotifications: [action.newNotification, ...state.popupNotifications], allNotifications: [action.newNotification, ...state.allNotifications] };
        case "addNotifications":
            return { popupNotifications: [...action.notifications, ...state.popupNotifications], allNotifications: [...action.notifications, ...state.allNotifications] };
    }
}

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(reducer, { popupNotifications: [], allNotifications: [] } as NotificationState);

    const notificationAPI = useMemo(() => {
        const clearNotifications = () => {
            dispatch({ type: "clearNotifications" });
        };
        const removeNotificationFromAll = (time: number) => {
            dispatch({ type: "removeNotificationFromAll", time: time });
        };
        const removeNotificationFromPopup = (time: number) => {
            dispatch({ type: "removeNotificationFromPopup", time: time });
        };
        const addNotification = (text: string, page: string, notificationType: NotificationType) => {
            const newNotification = { text: text, page: page, type: notificationType, time: Date.now() } as Notification;
            dispatch({ type: "addNotification", newNotification: newNotification });
        };
        const addNotifications = (notifications: Notification[]) => {
            dispatch({ type: "addNotifications", notifications: notifications });
        };

        return { clearNotifications, removeNotificationFromAll, removeNotificationFromPopup, addNotification, addNotifications }
    }, []);

    useInterval(() => {
        if (state.popupNotifications.length === 0) return;
        let mutateArray = structuredClone(state.popupNotifications);
        const removeAfterTime = Date.now() - NOTIFICATION_TIME;
        mutateArray.forEach(x => {
            if ((x.type === "success" || x.type === "info") && x.time < removeAfterTime) {
                notificationAPI.removeNotificationFromPopup(x.time);
            }
        })
    }, 1000);

    return (
        <NotificationAPIContext.Provider value={notificationAPI}>
            <NotificationDataContext.Provider value={state}>
                {children}
            </NotificationDataContext.Provider>
        </NotificationAPIContext.Provider>
    );
};

export const useNotificationData = () => useContext(NotificationDataContext);
export const useNotificationAPI = () => useContext(NotificationAPIContext);